/**
 * DownloadManager - Handles offline book downloads with DRM quota enforcement
 *
 * NEW: Chapter-by-chapter downloads with DRM quota checks
 * - User selects specific chapters to download
 * - DRM quota consumed at download time (per chapter)
 * - Supports partial book downloads
 * - Tracks downloadedAt timestamp per chapter for sliding window quotas
 *
 * Features:
 * - Chapter-by-chapter download with DRM enforcement
 * - Progress tracking
 * - Automatic encryption
 * - Error recovery
 * - Storage quota checks
 * - License caching
 */

import { offlineDatabase } from './OfflineDatabase';
import { encryptionService } from './EncryptionService';
import { storageManager } from './StorageManager';
import { drmQuotaService } from './DrmQuotaService';
import { resourceExtractor } from './ResourceExtractor';
import { booksService } from '@/api/booksService';
import { getUserLicenseForBook } from '@/api/licensesService';
import { LicenseType, LicenseStatus } from '@/types/library';
import type {
  OfflineBook,
  EpubChunk,
  ChapterResource,
  PdfPage,
  DownloadProgress,
  DownloadResult,
  DownloadError,
  OfflineLicense,
  BookFormat,
  DrmQuotaCache,
} from '@/types/offline';
import type { BookMetadataDto } from '@/types/book';

export class DownloadManager {
  private activeDownloads: Map<string, AbortController> = new Map();
  private progressCallbacks: Map<string, (progress: DownloadProgress) => void> = new Map();

  /**
   * Download selected chapters for offline reading (DRM-aware)
   * This is the NEW main entry point for downloads
   */
  async downloadSelectedChapters(
    bookId: string,
    userId: string,
    chapterIndices: number[],
    bookMetadata: BookMetadataDto,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<DownloadResult> {
    try {
      // Register progress callback
      if (onProgress) {
        this.progressCallbacks.set(bookId, onProgress);
      }

      // Create abort controller for cancellation
      const abortController = new AbortController();
      this.activeDownloads.set(bookId, abortController);

      // Initialize progress
      await this.updateProgress(bookId, {
        bookId,
        status: 'downloading',
        percentComplete: 0,
        downloadedChunks: 0,
        totalChunks: chapterIndices.length,
        downloadedBytes: 0,
        totalBytes: 0,
        startedAt: new Date(),
      });

      // Check if DRM enabled
      if (bookMetadata.isDrmEnabled) {
        // DRM enabled - request quota for each chapter
        const result = await drmQuotaService.requestMultipleChapters(
          userId,
          bookId,
          chapterIndices
        );

        if (result.rejectedChapters.length > 0) {
          console.warn(`${result.rejectedChapters.length} chapters rejected due to quota`);
        }

        // Download only approved chapters
        if (result.approvedChapters.length > 0) {
          await this.downloadEpubChapters(
            bookId,
            userId,
            result.approvedChapters,
            bookMetadata,
            abortController.signal
          );
        }

        // Cache DRM quota info
        await this.cacheDrmQuota(bookId, userId, result.remainingQuota);
      } else {
        // No DRM - download all selected chapters
        await this.downloadEpubChapters(
          bookId,
          userId,
          chapterIndices,
          bookMetadata,
          abortController.signal
        );
      }

      // Download highlights, notes, and bookmarks for offline viewing
      await this.downloadAnnotations(bookId);

      // Mark as complete
      await this.updateProgress(bookId, {
        bookId,
        status: 'complete',
        percentComplete: 100,
        downloadedChunks: chapterIndices.length,
        totalChunks: chapterIndices.length,
        downloadedBytes: 0,
        totalBytes: 0,
        completedAt: new Date(),
      });

      // Cleanup
      this.activeDownloads.delete(bookId);
      this.progressCallbacks.delete(bookId);

      return { success: true, bookId };
    } catch (error) {
      console.error('Download failed:', error);

      const downloadError: DownloadError = {
        code: this.getErrorCode(error),
        message: error instanceof Error ? error.message : 'Unknown error',
        recoverable: this.isRecoverable(error),
      };

      // Update progress with error
      await this.updateProgress(bookId, {
        bookId,
        status: 'failed',
        percentComplete: 0,
        downloadedChunks: 0,
        totalChunks: 0,
        downloadedBytes: 0,
        totalBytes: 0,
        error: downloadError.message,
      });

      // Cleanup
      this.activeDownloads.delete(bookId);
      this.progressCallbacks.delete(bookId);

      return {
        success: false,
        bookId,
        error: downloadError,
      };
    }
  }

  /**
   * Download EPUB chapters (with DRM quota already approved)
   */
  private async downloadEpubChapters(
    bookId: string,
    userId: string,
    chapterIndices: number[],
    bookMetadata: BookMetadataDto,
    signal: AbortSignal
  ): Promise<void> {
    if (chapterIndices.length === 0) {
      throw new Error('No chapters to download');
    }

    // Check storage quota
    const estimatedSize = chapterIndices.length * 500 * 1024; // Estimate 500KB per chapter
    const canDownload = await storageManager.canDownloadBook(estimatedSize);

    if (!canDownload.canDownload) {
      throw new Error(canDownload.reason || 'Not enough storage');
    }

    // Derive encryption keys
    const masterKey = await encryptionService.deriveMasterKey(userId, userId);
    const bookKey = await encryptionService.deriveBookKey(masterKey, bookId);

    // Download and encrypt selected chapters
    const chunks: EpubChunk[] = [];
    let totalBytes = 0;

    for (let i = 0; i < chapterIndices.length; i++) {
      if (signal.aborted) {
        throw new Error('Download cancelled');
      }

      const chapterIndex = chapterIndices[i];

      // Check if already downloaded
      const isDownloaded = await offlineDatabase.isChapterDownloaded(bookId, chapterIndex);
      if (isDownloaded) {
        console.log(`Chapter ${chapterIndex} already downloaded, skipping`);
        continue;
      }

      // Fetch chapter content (uses unified encryption endpoint that decrypts database chunks)
      const chapterData = await booksService.getChapterByIndex(bookId, chapterIndex);

      // Extract and download chapter resources (images, CSS, fonts)
      await this.downloadChapterResources(
        bookId,
        chapterIndex,
        chapterData.content,
        bookKey.key
      );

      // Convert content to ArrayBuffer
      const encoder = new TextEncoder();
      const contentBytes = encoder.encode(chapterData.content);

      // Encrypt chapter with client-side key for offline storage
      const encrypted = await encryptionService.encrypt(contentBytes, bookKey.key);

      // Create chunk with downloadedAt timestamp
      const chunk: EpubChunk = {
        id: `${bookId}-chapter-${chapterIndex}`,
        bookId,
        chapterIndex,
        chapterTitle: chapterData.title, // Title from database chunk metadata
        encryptedContent: encrypted.data,
        iv: encrypted.iv,
        sizeBytes: encrypted.data.byteLength,
        downloadedAt: new Date(), // CRITICAL: Track download timestamp for DRM
      };

      chunks.push(chunk);
      totalBytes += encrypted.data.byteLength;

      // Update progress
      const progress = ((i + 1) / chapterIndices.length) * 100;
      await this.updateProgress(bookId, {
        bookId,
        status: 'downloading',
        percentComplete: progress,
        downloadedChunks: i + 1,
        totalChunks: chapterIndices.length,
        downloadedBytes: totalBytes,
        totalBytes: estimatedSize,
      });
    }

    // Store all chunks in database
    await offlineDatabase.storeEpubChunks(chunks);

    // Update or create book metadata
    await this.updateBookMetadata(bookId, bookMetadata, totalBytes, chapterIndices.length);

    // Store license
    await this.storeLicense(bookId, userId);

    console.log(`✅ Downloaded ${chunks.length} chapters for ${bookMetadata.title} (${storageManager.formatBytes(totalBytes)})`);
  }

  /**
   * Download entire book (for non-DRM books)
   * Legacy method - downloads all chapters
   */
  async downloadAllChapters(
    bookId: string,
    userId: string,
    bookMetadata: BookMetadataDto,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<DownloadResult> {
    const allChapterIndices = bookMetadata.chapters.map((_, index) => index);
    return this.downloadSelectedChapters(
      bookId,
      userId,
      allChapterIndices,
      bookMetadata,
      onProgress
    );
  }

  /**
   * Download PDF (full document) - TODO: Add page-by-page DRM support
   */
  async downloadPdf(
    bookId: string,
    userId: string,
    bookMetadata: BookMetadataDto,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<DownloadResult> {
    // For now, PDF downloads entire document
    // TODO: Implement page-by-page downloads with DRM quota

    try {
      if (onProgress) {
        this.progressCallbacks.set(bookId, onProgress);
      }

      const abortController = new AbortController();
      this.activeDownloads.set(bookId, abortController);

      // Initialize progress
      await this.updateProgress(bookId, {
        bookId,
        status: 'downloading',
        percentComplete: 0,
        downloadedChunks: 0,
        totalChunks: 1,
        downloadedBytes: 0,
        totalBytes: 0,
        startedAt: new Date(),
      });

      // Fetch PDF blob
      const pdfBlob = await booksService.getPdfBlob(bookId);
      const estimatedSize = pdfBlob.byteLength;

      // Check storage quota
      const canDownload = await storageManager.canDownloadBook(estimatedSize);
      if (!canDownload.canDownload) {
        throw new Error(canDownload.reason || 'Not enough storage');
      }

      // Derive encryption keys
      const masterKey = await encryptionService.deriveMasterKey(userId, userId);
      const bookKey = await encryptionService.deriveBookKey(masterKey, bookId);

      // Encrypt PDF
      const encrypted = await encryptionService.encrypt(pdfBlob, bookKey.key);

      // Store as single PDF page
      const pdfPage: PdfPage = {
        id: `${bookId}-pdf-full`,
        bookId,
        pageRange: { start: 0, end: -1 },
        encryptedContent: encrypted.data,
        iv: encrypted.iv,
        sizeBytes: encrypted.data.byteLength,
        downloadedAt: new Date(),
      };

      await offlineDatabase.storePdfPage(pdfPage);

      // Update book metadata
      await this.updateBookMetadata(bookId, bookMetadata, encrypted.data.byteLength, bookMetadata.pageCount);

      // Store license
      await this.storeLicense(bookId, userId);

      // Mark complete
      await this.updateProgress(bookId, {
        bookId,
        status: 'complete',
        percentComplete: 100,
        downloadedChunks: 1,
        totalChunks: 1,
        downloadedBytes: encrypted.data.byteLength,
        totalBytes: encrypted.data.byteLength,
        completedAt: new Date(),
      });

      this.activeDownloads.delete(bookId);
      this.progressCallbacks.delete(bookId);

      return { success: true, bookId };
    } catch (error) {
      console.error('PDF download failed:', error);
      return {
        success: false,
        bookId,
        error: {
          code: 'UNKNOWN',
          message: error instanceof Error ? error.message : 'Unknown error',
          recoverable: false,
        },
      };
    }
  }

  /**
   * Download and store chapter resources (images, CSS, fonts)
   */
  private async downloadChapterResources(
    bookId: string,
    chapterIndex: number,
    chapterHtml: string,
    bookKey: CryptoKey
  ): Promise<void> {
    try {
      // Extract resources from HTML
      const baseUrl = window.location.origin; // API base URL
      const extractedResources = resourceExtractor.extractResources(chapterHtml, baseUrl);

      if (extractedResources.length === 0) {
        console.log(`📦 No resources found in chapter ${chapterIndex}`);
        return;
      }

      console.log(`📦 Downloading ${extractedResources.length} resources for chapter ${chapterIndex}...`);

      // Download and encrypt each resource
      const chapterResources: ChapterResource[] = [];

      for (let i = 0; i < extractedResources.length; i++) {
        const resource = extractedResources[i];

        try {
          // Fetch resource from server (assuming resources are served from API or public path)
          console.log(`  📥 Fetching resource ${i + 1}/${extractedResources.length}: ${resource.url}`);
          const response = await fetch(resource.url);

          console.log(`     → Status: ${response.status} ${response.statusText}`);
          console.log(`     → Content-Type: ${response.headers.get('content-type')}`);
          console.log(`     → Content-Length: ${response.headers.get('content-length')}`);

          if (!response.ok) {
            console.warn(`❌ Failed to download resource: ${resource.url} (${response.status})`);
            continue;
          }

          const resourceBlob = await response.blob();
          const resourceBytes = await resourceBlob.arrayBuffer();

          console.log(`     → Blob size: ${resourceBlob.size} bytes`);
          console.log(`     → ArrayBuffer size: ${resourceBytes.byteLength} bytes`);
          console.log(`     → Blob type: ${resourceBlob.type}`);

          // Encrypt resource
          const encrypted = await encryptionService.encrypt(resourceBytes, bookKey);

          console.log(`     → Encrypted size: ${encrypted.data.byteLength} bytes`);

          // Create chapter resource entry
          const chapterResource: ChapterResource = {
            id: `${bookId}-chapter-${chapterIndex}-resource-${i}`,
            bookId,
            chapterIndex,
            originalUrl: resource.url,
            downloadUrl: resource.url, // Same as originalUrl for now
            resourceType: resource.type,
            mimeType: resourceExtractor.getMimeType(resource.url),
            encryptedContent: encrypted.data,
            iv: encrypted.iv,
            sizeBytes: encrypted.data.byteLength,
            downloadedAt: new Date(),
          };

          chapterResources.push(chapterResource);
          console.log(`     ✅ Stored ${resource.type}: encrypted=${encrypted.data.byteLength} bytes\n`);
        } catch (err) {
          console.error(`❌ Failed to download resource ${resource.url}:`, err);
          // Continue with other resources even if one fails
        }
      }

      // Store all resources in database
      if (chapterResources.length > 0) {
        await offlineDatabase.storeChapterResources(chapterResources);
        console.log(`💾 Stored ${chapterResources.length} resources for chapter ${chapterIndex}`);
      }
    } catch (error) {
      console.error(`Failed to download chapter resources for chapter ${chapterIndex}:`, error);
      // Don't throw - chapter can still work without resources (just missing images/CSS)
    }
  }

  /**
   * Update or create book metadata
   */
  private async updateBookMetadata(
    bookId: string,
    bookMetadata: BookMetadataDto,
    sizeBytes: number,
    chapterCount?: number
  ): Promise<void> {
    const existingBook = await offlineDatabase.getBook(bookId);

    if (existingBook) {
      // Update existing book
      existingBook.sizeBytes += sizeBytes;
      existingBook.lastAccessed = new Date();
      if (chapterCount) {
        existingBook.chapterCount = (existingBook.chapterCount || 0) + chapterCount;
      }
      await offlineDatabase.storeBook(existingBook);
    } else {
      // Create new book entry
      const offlineBook: OfflineBook = {
        id: bookId,
        title: bookMetadata.title,
        author: bookMetadata.author,
        format: bookMetadata.format === 1 ? 'EPUB' : 'PDF',
        downloadedAt: new Date(),
        lastAccessed: new Date(),
        sizeBytes,
        chapterCount,
        pageCount: bookMetadata.pageCount,
        coverImageUrl: bookMetadata.coverImageUrl,
        encryptionMetadata: {
          algorithm: 'AES-GCM',
          keyId: `book-${bookId}`,
          ivBase: new ArrayBuffer(0),
        },
      };

      await offlineDatabase.storeBook(offlineBook);
    }
  }

  /**
   * Store license for offline use
   */
  private async storeLicense(bookId: string, userId: string): Promise<void> {
    const apiLicense = await getUserLicenseForBook(bookId);

    if (!apiLicense) {
      throw new Error('Unable to verify your license. Please try again.');
    }

    const license: OfflineLicense = {
      id: `${bookId}-${userId}`,
      bookId,
      userId,
      deviceId: await this.getDeviceId(),
      createdAt: new Date(),
      licenseType: apiLicense.licenseType,
      status: apiLicense.status,
      expirationDate: apiLicense.expirationDate,
    };

    await offlineDatabase.storeLicense(license);
  }

  /**
   * Cache DRM quota information
   */
  private async cacheDrmQuota(
    bookId: string,
    userId: string,
    remainingQuota: number
  ): Promise<void> {
    const quotaCache: DrmQuotaCache = {
      id: `${bookId}-${userId}`,
      bookId,
      userId,
      isDrmEnabled: true,
      quotaLimit: remainingQuota + 1, // Rough estimate
      quotaPeriodDays: 7, // Default
      isUnlimited: remainingQuota > 999999,
      cachedAt: new Date(),
      lastSynced: new Date(),
    };

    await offlineDatabase.storeDrmQuota(quotaCache);
  }

  /**
   * Cancel active download
   */
  cancelDownload(bookId: string): void {
    const abortController = this.activeDownloads.get(bookId);
    if (abortController) {
      abortController.abort();
      this.activeDownloads.delete(bookId);
      this.progressCallbacks.delete(bookId);
      console.log(`🛑 Download cancelled: ${bookId}`);
    }
  }

  /**
   * Get active downloads
   */
  getActiveDownloads(): string[] {
    return Array.from(this.activeDownloads.keys());
  }

  /**
   * Check if book is currently downloading
   */
  isDownloading(bookId: string): boolean {
    return this.activeDownloads.has(bookId);
  }

  /**
   * Get download progress
   */
  async getProgress(bookId: string): Promise<DownloadProgress | undefined> {
    return offlineDatabase.getDownloadProgress(bookId);
  }

  /**
   * Update download progress
   */
  private async updateProgress(bookId: string, progress: DownloadProgress): Promise<void> {
    await offlineDatabase.updateDownloadProgress(progress);

    // Call progress callback if registered
    const callback = this.progressCallbacks.get(bookId);
    if (callback) {
      callback(progress);
    }
  }

  /**
   * Get device ID (for license tracking)
   */
  private async getDeviceId(): Promise<string> {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  /**
   * Determine error code from exception
   */
  private getErrorCode(error: unknown): DownloadError['code'] {
    if (error instanceof Error) {
      if (error.message.includes('license')) return 'NETWORK_ERROR';
      if (error.message.includes('storage')) return 'STORAGE_FULL';
      if (error.message.includes('quota')) return 'QUOTA_EXCEEDED';
      if (error.message.includes('network') || error.message.includes('fetch')) return 'NETWORK_ERROR';
      if (error.message.includes('encrypt')) return 'ENCRYPTION_ERROR';
    }
    return 'UNKNOWN';
  }

  /**
   * Download highlights, notes, and bookmarks for offline viewing
   * Fetches from API and stores in IndexedDB
   */
  private async downloadAnnotations(bookId: string): Promise<void> {
    try {
      console.log(`📝 Downloading annotations (highlights/notes/bookmarks) for book ${bookId}...`);

      // Import API services dynamically to avoid circular dependencies
      const { highlightsService } = await import('@/api/highlightsService');
      const { bookmarksService } = await import('@/api/bookmarksService');
      const { offlineContentService } = await import('./OfflineContentService');

      // Fetch highlights and notes from API
      const highlightDtos = await highlightsService.getMyHighlights(bookId);
      console.log(`  ✅ Fetched ${highlightDtos.length} highlights/notes from API`);

      // Convert HighlightDto to HighlightData/NoteData
      const highlights: import('@/types/offline').HighlightData[] = [];
      const notes: import('@/types/offline').NoteData[] = [];

      highlightDtos.forEach((dto) => {
        // Check if it's a page note (no selected text) or a highlight (has selected text)
        const isPageNote = dto.selectedText === '[PAGE NOTE]' || dto.selectedText === '';

        if (isPageNote) {
          // It's a note
          notes.push({
            id: dto.id,
            bookId: dto.bookId,
            location: dto.location,
            content: dto.noteText || '',
            createdAt: new Date(dto.creationTime),
            updatedAt: new Date(dto.creationTime),
          });
        } else {
          // It's a highlight
          highlights.push({
            id: dto.id,
            bookId: dto.bookId,
            location: dto.location,
            text: dto.selectedText,
            color: this.mapBackendColorToHex(dto.color),
            note: dto.noteText,
            createdAt: new Date(dto.creationTime),
          });
        }
      });

      // Fetch bookmarks from API
      const bookmarkDtos = await bookmarksService.getMyBookmarks(bookId);
      console.log(`  ✅ Fetched ${bookmarkDtos.length} bookmarks from API`);

      // Convert BookmarkDto to BookmarkData
      const bookmarks: import('@/types/offline').BookmarkData[] = bookmarkDtos.map((dto) => ({
        id: dto.id,
        bookId: dto.bookId,
        location: dto.location,
        label: dto.locationLabel,
        createdAt: new Date(dto.creationTime),
      }));

      // Store all annotations in offline storage
      await offlineContentService.syncAllAnnotationsToOffline(bookId, highlights, notes, bookmarks);

      console.log(`  💾 Stored ${highlights.length} highlights, ${notes.length} notes, ${bookmarks.length} bookmarks offline`);
    } catch (error) {
      console.error(`Failed to download annotations for book ${bookId}:`, error);
      // Don't throw - book can still work offline without annotations
    }
  }

  /**
   * Map backend HighlightColor enum to frontend hex color
   */
  private mapBackendColorToHex(backendColor: number): string {
    const colorMap: Record<number, string> = {
      1: '#FFEB3B', // Yellow
      2: '#4CAF50', // Green
      3: '#2196F3', // Blue
      4: '#E91E63', // Pink
      5: '#9C27B0', // Purple
      6: '#FF9800', // Orange
      7: '#F44336', // Red
      8: '#009688', // Teal
      9: '#CDDC39', // Lime
      10: '#FFC107', // Amber
    };
    return colorMap[backendColor] || '#FFEB3B'; // Default to Yellow
  }

  /**
   * Check if error is recoverable
   */
  private isRecoverable(error: unknown): boolean {
    const code = this.getErrorCode(error);
    return code === 'NETWORK_ERROR';
  }
}

// Singleton instance
export const downloadManager = new DownloadManager();
